// MARK://dhk8/pos/lib/clients/media
// Typed client for dhk8-media — uploads de soportes (fotos de ticket, PDF, XML)
// a Akamai Object Storage (S3-compatible). Reconciliado contra
// services/media/src/api/media.routes.ts (POST /v1/media/upload).
//
// Contrato: multipart (campo `file`, opcional `namespace`) O json base64.
//   Auth: header `x-dhk8-tenant-id` + `Authorization: Bearer <jwt>` (los pone apiFetch).
//   Respuesta 201 { ok:true, data:{ key, url, contentType } }.
//   4xx { ok:false, error:{ code, message } } (apiFetch lanza ApiError con el message).
//
// Ingress route: /v1/dhk8-media/v1/media/upload → el servicio ve /v1/media/upload.
//
// NOTA (backend): el StorageService valida content-type contra ALLOWED_IMAGE_TYPES;
// hoy sólo imágenes. PDF/XML pueden devolver 415 hasta que el backend amplíe la
// lista — la UI ofrece adjuntarlos igual (son OPCIONALES) y `uploadMedia` propaga
// el mensaje de error del servicio si los rechaza.

import { apiFetch, svcUrl } from '../api.js'

export interface MediaUploadResult {
  key: string
  url: string
  contentType: string
}

type Envelope<T> = { ok: true; data: T } | { ok: false; error: { code: string; message: string } }

const UPLOAD_URL = svcUrl('dhk8-media', '/v1/media/upload')

/**
 * Sube un archivo a dhk8-media y devuelve su `{ key, url, contentType }`. La `key`
 * es la que se persiste (p.ej. en attachmentKeys de una compra petty-cash). El
 * `namespace` agrupa la key bajo `tenants/<tenantId>/<namespace>/...` (default del
 * servicio si se omite). Lanza ApiError con el mensaje del servicio si falla.
 */
export async function uploadMedia(file: File, namespace?: string): Promise<MediaUploadResult> {
  const form = new FormData()
  form.append('file', file)
  if (namespace && namespace.length > 0) form.append('namespace', namespace)

  // apiFetch detecta FormData → NO fuerza Content-Type (el browser pone el
  // boundary) y adjunta tenant + bearer headers.
  const body = await apiFetch<Envelope<MediaUploadResult> | MediaUploadResult>(UPLOAD_URL, {
    method: 'POST',
    body: form,
  })
  if (body !== null && typeof body === 'object' && 'ok' in body) {
    const env = body as Envelope<MediaUploadResult>
    if (env.ok) return env.data
    throw new Error(`${env.error.code}: ${env.error.message}`)
  }
  return body as MediaUploadResult
}
